/**
 * Build LP participant-profile view model from API data + HTML mock structure.
 */

import { formatScheduleDate, formatParticipantNationality } from './orgLpHelpers';
import { immigrationStatusCell, needsImmigration } from './lpProductionHelpers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const IMM_STAGES = [
  'Eligibility questionnaire',
  'Documents submitted',
  'Under review',
  'Submitted to authority',
  'Awaiting decision',
  'Approved',
];

function parseIsoDate(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtShort(d) {
  if (!d) return '-';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function hasPassportOnFile(checklist, caseInfo) {
  if ((checklist || []).some((d) => d?.key === 'passport' && d?.on_file)) {
    return true;
  }
  const types = caseInfo?.document_types;
  if (Array.isArray(types)) {
    return types.some((t) => String(t || '').toLowerCase().includes('passport'));
  }
  return false;
}

function hasTravelItinerarySubmitted(apiItinerary) {
  if (!apiItinerary) return false;
  if (apiItinerary.completed) return true;
  const steps = Array.isArray(apiItinerary.steps) ? apiItinerary.steps : [];
  return steps.length > 0;
}

function stageIndex(participant, caseInfo, checklist, apiItinerary) {
  const bucket = participant?.immigration_bucket;
  const status = String(caseInfo?.case_status || participant?.case_status || '');
  const passportOk = hasPassportOnFile(checklist, caseInfo);
  const itineraryOk = hasTravelItinerarySubmitted(apiItinerary);
  // Insurance is not implemented yet - never required for stage progression.
  const docsSubmitted = passportOk && itineraryOk;
  const anyDocStarted = passportOk || itineraryOk;

  if (
    bucket === 'complete'
    || status === 'decision'
    || status === 'complete'
    || status === 'approved'
    || status === 'completed'
  ) {
    return 5;
  }
  if (status === 'submitted' || status === 'in_review_govt') {
    return docsSubmitted ? 3 : (anyDocStarted ? 1 : 0);
  }
  if (status === 'in_review' || status === 'ready_for_review' || status === 'ready_to_submit') {
    return docsSubmitted ? 2 : (anyDocStarted ? 1 : 0);
  }
  // "Documents submitted" only when passport + travel itinerary are actually in.
  if (docsSubmitted) return 1;
  return 0;
}

/**
 * Map uploaded CSV itinerary steps from the API into the LP timeline UI shape.
 */
function mapApiItinerary(apiItinerary) {
  const steps = Array.isArray(apiItinerary?.steps) ? apiItinerary.steps : [];
  return steps
    .map((step) => ({
      type: String(step?.type || 'other').trim().toLowerCase() || 'other',
      date: String(step?.date || '').trim(),
      title: String(step?.title || '').trim(),
      subtitle: String(step?.subtitle || '').trim(),
      details: (Array.isArray(step?.details) ? step.details : [])
        .filter((pair) => Array.isArray(pair) && pair.length >= 2)
        .map(([k, v]) => [String(k), String(v)]),
    }))
    .filter((step) => step.date && step.title);
}

/** "8 Aug 2026" - the long form used across the participant profile. */
function fmtLongDate(iso) {
  const d = parseIsoDate(iso);
  if (!d) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtHistoryDate(iso) {
  if (!iso) return '-';
  const d = parseIsoDate(iso) || (iso instanceof Date ? iso : null);
  if (d) return fmtShort(d);
  const s = String(iso);
  const parsed = parseIsoDate(s.slice(0, 10));
  return parsed ? fmtShort(parsed) : formatScheduleDate(s.slice(0, 10)) || s.slice(0, 10);
}

function mapApiTodos(apiTodos, participant, caseInfo) {
  if (Array.isArray(apiTodos) && apiTodos.length) {
    return apiTodos.map((t) => ({
      key: t.key,
      name: t.name,
      done: Boolean(t.done),
      comingSoon: Boolean(t.coming_soon),
      immigrationSteps: Boolean(t.immigration_steps),
      history: (t.history || []).map((h) => [
        fmtHistoryDate(h.at),
        h.label || '',
      ]),
      requestedAt: t.requested_at || null,
      completedAt: t.completed_at || null,
    }));
  }
  // Fallback if an older API payload has no todos yet.
  return buildTodosLegacy(participant, caseInfo);
}

function buildTodosLegacy(participant, caseInfo) {
  const registered = participant?.status === 'registered' || Boolean(participant?.registered_at);
  const immDone = participant?.immigration_bucket === 'complete'
    || caseInfo?.case_status === 'complete'
    || caseInfo?.case_status === 'decision';

  const rows = [
    {
      key: 'reg',
      name: 'Complete registration',
      done: registered,
      history: registered
        ? [[fmtHistoryDate(participant?.registered_at), 'Completed']]
        : [['-', 'Outstanding']],
    },
    {
      key: 'itin',
      name: 'Upload travel itinerary',
      done: false,
      history: [['-', 'Outstanding']],
    },
  ];

  if (registered) {
    rows.push({
      key: 'immg',
      name: 'Complete immigration',
      done: immDone,
      immigrationSteps: true,
    });
  }
  return rows;
}

function statusTone(done, inProgress = false) {
  if (done) return { status: 'Complete', tone: 'ok' };
  if (inProgress) return { status: 'In progress', tone: 'warn' };
  return { status: 'Outstanding', tone: 'warn' };
}

/**
 * Map portal section flags → LP person-page status rows.
 * Groups and labels match the participant app home (fieldwork).
 * Professional is "In progress" when personal is done but professional is not yet marked complete.
 */
function buildSectionStatusRows(sections, checklist, caseInfo) {
  const s = sections || {};
  const personalDone = Boolean(s.personal || s.contact);
  const detailsDone = Boolean(s.details);
  const passportOk = Boolean(s.passport_details) || hasPassportOnFile(checklist, caseInfo);

  return {
    professional: [
      {
        key: 'details',
        label: 'Professional profile',
        ...statusTone(detailsDone, personalDone && !detailsDone),
      },
    ],
    details: [
      { key: 'personal', label: 'Personal details', ...statusTone(personalDone) },
      { key: 'online', label: 'Social media', ...statusTone(Boolean(s.online)) },
      { key: 'work', label: 'Your work on this production', ...statusTone(Boolean(s.work)) },
    ],
    travel: [
      { key: 'passport', label: 'Passport', ...statusTone(passportOk) },
      { key: 'otherdocs', label: 'Other travel documents', ...statusTone(Boolean(s.otherdocs)) },
    ],
    immigration: [
      { key: 'family', label: 'Accompanying family members', ...statusTone(Boolean(s.family)) },
      { key: 'immhistory', label: 'Immigration details', ...statusTone(Boolean(s.immhistory)) },
      { key: 'legal', label: 'Legal history', ...statusTone(Boolean(s.legal)) },
    ],
  };
}

function buildDocuments(checklist, caseInfo, apiItinerary) {
  const passportOk = hasPassportOnFile(checklist, caseInfo);
  const itineraryOk = hasTravelItinerarySubmitted(apiItinerary);

  const row = (label, submitted) => ({
    label,
    status: submitted ? 'Submitted' : 'Pending',
    tone: submitted ? 'ok' : 'warn',
  });

  return [
    row('Passport', passportOk),
    row('Travel itinerary', itineraryOk),
  ];
}

function buildResponsiveness(todos, timeline) {
  const openItems = todos.filter((t) => !t.done).length;
  const history = (timeline || [])
    .filter((e) => e.done && e.at)
    .map((e) => ({
      date: formatScheduleDate(String(e.at).slice(0, 10)) || fmtShort(parseIsoDate(e.at)),
      name: e.label,
      meta: 'Completed',
    }));

  const fallback = todos.map((t) => ({
    date: fmtHistoryDate(t.completedAt || t.requestedAt),
    name: t.name,
    meta: t.done ? 'Completed' : 'Open',
  }));

  const rows = history.length ? history : fallback;
  const doneMetas = rows.filter((r) => !String(r.meta).startsWith('Open'));
  const avgResp = 0;
  const avgRem = 0;

  return { avgResp, avgRem, openItems, history: rows, doneCount: doneMetas.length };
}

/**
 * Travel itinerary (from uploaded CSV) is Mobility → Immigration only, for registered users.
 * Production roster person pages must not show it.
 */
function canShowTravelItinerary({ registration, participant, fromImmigration, itinerary }) {
  if (!fromImmigration) return false;
  const registered = Boolean(
    registration?.completed
    || participant?.status === 'registered'
    || participant?.registered_at,
  );
  if (!registered) return false;
  const steps = mapApiItinerary(itinerary);
  return steps.length > 0 || Boolean(itinerary?.completed);
}

/**
 * @returns {object} view model for OrgParticipantDetail
 */
export function buildParticipantProfileView({
  project,
  participant,
  caseInfo,
  timeline,
  checklist,
  todos: apiTodos,
  openItemsCount,
  nda,
  safety,
  registration,
  itinerary: apiItinerary,
  sections: apiSections,
  fromImmigration = false,
}) {
  const p = participant || {};
  const registered = needsImmigration(p);
  const sIdx = stageIndex(p, caseInfo, checklist, apiItinerary);
  const todos = mapApiTodos(apiTodos, p, caseInfo)
    .filter((t) => t.key !== 'nda' && t.key !== 'safety');
  const openItems = typeof openItemsCount === 'number'
    ? openItemsCount
    : todos.filter((t) => !t.done).length;
  const regStatus = registration || null;
  const itinerary = canShowTravelItinerary({
    registration: regStatus,
    participant: p,
    fromImmigration,
    itinerary: apiItinerary,
  })
    ? mapApiItinerary(apiItinerary)
    : [];
  const documents = buildDocuments(checklist, caseInfo, apiItinerary);
  const sectionRows = buildSectionStatusRows(apiSections, checklist, caseInfo);
  const responsiveness = buildResponsiveness(todos, timeline);
  const nationality = formatParticipantNationality(p.country) || p.country || '-';
  const role = p.job_title || p.department || 'Crew';
  const itinTodo = todos.find((t) => t.key === 'itin');
  const itineraryStatus = statusTone(
    Boolean(itinTodo?.done) || hasTravelItinerarySubmitted(apiItinerary),
  );

  return {
    name: p.name || p.email || 'Participant',
    role,
    nationality,
    neededBy: fmtLongDate(p.schedule_start_date),
    status: immigrationStatusCell(p),
    productionName: project?.name || 'Production',
    openItems,
    itinerary,
    sectionRows,
    productionRows: [
      { key: 'itin', label: 'Travel itinerary', ...itineraryStatus },
    ],
    todos: todos.map((t) => ({
      ...t,
      // The design shows a date under each task; we only hold the real
      // request / completion timestamps, so surface whichever applies.
      subLabel: t.done
        ? `Completed ${fmtLongDate(t.completedAt)}`.trim()
        : (t.requestedAt ? `Requested ${fmtLongDate(t.requestedAt)}` : ''),
    })),
    registration: registration || null,
    nda: nda || null,
    safety: safety || null,
    // Always present: the profile shows an Immigration panel even before the
    // person registers, where the stage has simply not begun.
    // Route = invite procedure_slug (nationality × shoot country) → gm-content label.
    immigration: {
      route: p.procedure_label || p.procedure_slug || 'Pending',
      advisor: 'Not assigned',
      stage: registered ? IMM_STAGES[sIdx] : 'Not started',
    },
    documents,
    responsiveness: {
      ...responsiveness,
      openItems,
    },
  };
}
